import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { cn, formatRelativeTime } from "../../../lib/utils";
import type { AuditEvent } from "../../../models/audit/auditEvent";
import { AuditEventDetail, hasDetail } from "./AuditEventDetail";
import { AuditEventIcon } from "./AuditEventIcon";
import { buildEventContext } from "./eventContext";
import { resolveActor } from "./resolveActor";

interface AuditEventRowProps {
  event: AuditEvent;
  /** Whether this is the last row in its list — suppresses the trailing
   * timeline connector. */
  isLast: boolean;
  memberEmailById?: (userId: string) => string | undefined;
}

/**
 * One row in an audit event feed: icon chip, title, actor + relative time,
 * an optional context line (test/sample hints not already stated in the
 * title), and — only when the event carries renderable detail — an expand
 * chevron revealing `AuditEventDetail`.
 */
export function AuditEventRow({
  event,
  isLast,
  memberEmailById,
}: AuditEventRowProps) {
  const [expanded, setExpanded] = useState(false);
  const actor = resolveActor(event, memberEmailById);
  const expandable = hasDetail(event);

  const context = buildEventContext(event);

  return (
    <div className="relative flex gap-3 text-xs text-font-secondary">
      {!isLast && (
        <div className="absolute bottom-0 left-3 top-6 w-px bg-stone-200" />
      )}
      <AuditEventIcon eventType={event.eventType} className="relative z-10" />
      <div className="min-w-0 flex-1 pt-0.5">
        <div
          onClick={expandable ? () => setExpanded((prev) => !prev) : undefined}
          className={cn(
            "flex items-start justify-between gap-2",
            expandable && "cursor-pointer",
          )}
        >
          <div className="min-w-0">
            <div className="flex items-center gap-1 font-medium text-stone-900">
              {expandable &&
                (expanded ? (
                  <ChevronDown className="h-3 w-3 flex-shrink-0 text-stone-400" />
                ) : (
                  <ChevronRight className="h-3 w-3 flex-shrink-0 text-stone-400" />
                ))}
              <span className="truncate">{event.title}</span>
            </div>
            <div className="mt-0.5 text-stone-500">
              {actor}
              {context && <span> · {context}</span>}
            </div>
          </div>
          <span className="flex-shrink-0 whitespace-nowrap text-stone-500">
            {formatRelativeTime(event.createdAt)}
          </span>
        </div>
        {expandable && expanded && (
          <div className="mt-2">
            <AuditEventDetail event={event} memberEmailById={memberEmailById} />
          </div>
        )}
      </div>
    </div>
  );
}
